import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { IconCheck } from "@tabler/icons-react";
import ReactGA from "react-ga4";

import AiApi from "../api/ai";
import TeamsApi from "../api/teams";
import { getAiSetupStatus, SetupItemKey } from "../utils/ai-setup-status";
import styles from "./NavBarAiItem.module.css";

type SetupChecklistItem = {
  key: SetupItemKey;
  done: boolean;
  path?: string;
  href?: string;
  labelId: string;
  descriptionId: string;
};

type NavBarAiItemProps = {
  path: string;
  companyId?: string | number;
};

const NavBarAiItem: React.FC<NavBarAiItemProps> = ({ path, companyId }) => {
  const intl = useIntl();
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [showCompletionBanner, setShowCompletionBanner] = useState(false);
  const [mobileAppsDone, setMobileAppsDone] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const previousActivationBlockedRef = useRef<boolean | null>(null);
  const mobileAppsStorageKey = useMemo(
    () =>
      companyId
        ? `ai_setup_mobile_apps_downloaded:${companyId}`
        : "ai_setup_mobile_apps_downloaded",
    [companyId]
  );
  const mobileAppsUrl = useMemo(() => {
    if (typeof navigator !== "undefined") {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("android")) {
        return "https://play.google.com/store/apps/details?id=visito.app.production";
      }
    }
    return "https://apps.apple.com/app/id6475635950";
  }, []);

  const { data: aiStatusData } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });
  const { data: teamData } = useQuery({
    queryFn: () => TeamsApi.getInvite(),
    queryKey: ["getTeamInvites"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const teamMembersDone = useMemo(() => {
    const list = Array.isArray(teamData?.data?.list) ? teamData.data.list : [];

    const eligibleMembers = list.filter((member: any) => {
      const role = String(member?.role || "").toLowerCase();
      const accepted = member?.accepted !== false;
      return accepted && role !== "system";
    });

    const adminCount = eligibleMembers.filter(
      (member: any) => String(member?.role || "").toLowerCase() === "admin",
    ).length;
    const userCount = eligibleMembers.filter(
      (member: any) => String(member?.role || "").toLowerCase() === "user",
    ).length;

    return adminCount > 1 || (adminCount >= 1 && userCount >= 1);
  }, [teamData?.data?.list]);

  const aiSetupStatus = useMemo(
    () =>
      getAiSetupStatus(aiStatusData?.status, {
        includeExtendedSteps: true,
        teamMembersDone,
        mobileAppsDone,
      }),
    [aiStatusData?.status, teamMembersDone, mobileAppsDone]
  );

  const setupChecklistItems = useMemo<SetupChecklistItem[]>(
    () => [
      {
        key: "knowledge",
        done: aiSetupStatus.knowledgeDone,
        path: "/ai/website",
        labelId: "nav.aiSetup.item.knowledge",
        descriptionId: "nav.aiSetup.item.knowledge.desc",
      },
      {
        key: "integrations",
        done: aiSetupStatus.integrationsDone,
        path: "/ai/integrations",
        labelId: "nav.aiSetup.item.integrations",
        descriptionId: "nav.aiSetup.item.integrations.desc",
      },
      {
        key: "channels",
        done: aiSetupStatus.channelsDone,
        path: "/ai/channels",
        labelId: "nav.aiSetup.item.channels",
        descriptionId: "nav.aiSetup.item.channels.desc",
      },
      {
        key: "teamMembers",
        done: aiSetupStatus.teamMembersDone,
        path: "/settings/team",
        labelId: "nav.aiSetup.item.teamMembers",
        descriptionId: "nav.aiSetup.item.teamMembers.desc",
      },
      {
        key: "mobileApps",
        done: aiSetupStatus.mobileAppsDone,
        href: mobileAppsUrl,
        labelId: "nav.aiSetup.item.mobileApps",
        descriptionId: "nav.aiSetup.item.mobileApps.desc",
      },
    ],
    [aiSetupStatus, mobileAppsUrl]
  );

  const shouldShowSetupChip =
    Boolean(aiStatusData?.status) &&
    aiSetupStatus.isActivationBlockedByChannel &&
    aiSetupStatus.pendingItems.length > 0;

  const completionStorageKey = useMemo(
    () =>
      companyId
        ? `ai_setup_completion_seen:${companyId}`
        : "ai_setup_completion_seen",
    [companyId]
  );

  useEffect(() => {
    if (!isPopoverOpen) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!shouldShowSetupChip && isPopoverOpen) {
      setPopoverOpen(false);
    }
  }, [shouldShowSetupChip, isPopoverOpen]);

  useEffect(() => {
    if (!showCompletionBanner) return;
    const timeout = window.setTimeout(() => {
      setShowCompletionBanner(false);
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [showCompletionBanner]);

  useEffect(() => {
    try {
      setMobileAppsDone(localStorage.getItem(mobileAppsStorageKey) === "1");
    } catch (_) {
      setMobileAppsDone(false);
    }
  }, [mobileAppsStorageKey]);

  useEffect(() => {
    if (!aiStatusData?.status) return;

    const isActivationBlocked = aiSetupStatus.isActivationBlockedByChannel;
    const wasActivationBlocked = previousActivationBlockedRef.current;

    if (wasActivationBlocked === null) {
      previousActivationBlockedRef.current = isActivationBlocked;
      return;
    }

    if (wasActivationBlocked && !isActivationBlocked) {
      let alreadySeen = false;
      try {
        alreadySeen = localStorage.getItem(completionStorageKey) === "1";
      } catch (_) {
        alreadySeen = false;
      }

      if (!alreadySeen) {
        setPopoverOpen(false);
        setShowCompletionBanner(true);
        // @ts-ignore custom analytics event
        ReactGA.event("navbar_setup_completed", {
          companyId,
          destination: "inbox",
        });

        try {
          localStorage.setItem(completionStorageKey, "1");
        } catch (_) {
          // no-op
        }
      }
    }

    previousActivationBlockedRef.current = isActivationBlocked;
  }, [
    aiStatusData?.status,
    aiSetupStatus.isActivationBlockedByChannel,
    completionStorageKey,
    companyId,
  ]);

  useEffect(() => {
    setPopoverOpen(false);
  }, [path]);

  if (!shouldShowSetupChip && !showCompletionBanner) {
    return null;
  }

  return (
    <div className={styles.navAiSetupItem}>
      {showCompletionBanner ? (
        <div className={styles.completionBanner} role="status">
          <div className={styles.completionText}>
            <span className={styles.completionTitle}>
              <FormattedMessage id="nav.aiSetup.completed.title" />
            </span>
            <span className={styles.completionSubtitle}>
              <FormattedMessage id="nav.aiSetup.completed.subtitle" />
            </span>
          </div>
          <Link
            className={styles.completionCta}
            to="/chats"
            onClick={() => setShowCompletionBanner(false)}
          >
            <FormattedMessage id="nav.aiSetup.completed.cta" />
          </Link>
        </div>
      ) : (
        <div className={styles.setupWrap} ref={popoverRef}>
          <button
            type="button"
            className={styles.setupButton}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setPopoverOpen((prev) => {
                const next = !prev;
                if (next) {
                  // @ts-ignore custom analytics event
                  ReactGA.event("navbar_setup_opened", { companyId });
                }
                return next;
              });
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setPopoverOpen(false);
              }
            }}
            aria-haspopup="dialog"
            aria-expanded={isPopoverOpen}
          >
            <div
              className={styles.progressCircle}
              style={{
                background: `conic-gradient(var(--visito-color-info) ${(aiSetupStatus.completedCount / aiSetupStatus.totalCount) * 100}%, var(--ion-color-step-100) 0)`,
              }}
              aria-hidden="true"
            >
              <div className={styles.progressInner}>
                {aiSetupStatus.completedCount}/{aiSetupStatus.totalCount}
              </div>
            </div>
            <div className={styles.setupText}>
              <span className={styles.setupTitle}>
                <FormattedMessage id="nav.aiSetup.title" />
              </span>
              <span className={styles.setupSubtitle}>
                <FormattedMessage
                  id={
                    aiSetupStatus.isActivationBlockedByChannel
                      ? "nav.aiSetup.progressSummary.blocked"
                      : "nav.aiSetup.progressSummary"
                  }
                  values={{
                    completed: aiSetupStatus.completedCount,
                    total: aiSetupStatus.totalCount,
                  }}
                />
              </span>
            </div>
          </button>
          {isPopoverOpen ? (
            <div
              className={styles.setupPopover}
              role="dialog"
              aria-label={intl.formatMessage({
                id: "nav.aiSetup.popover.title",
              })}
            >
              <div className={styles.popoverTitle}>
                <FormattedMessage id="nav.aiSetup.popover.title" />
              </div>
              <div className={styles.popoverSubtitle}>
                <FormattedMessage id="nav.aiSetup.popover.subtitle" />
              </div>

              <div className={styles.setupList}>
                {setupChecklistItems.map(
                  ({ key, done, path: itemPath, href, labelId, descriptionId }) => {
                    const className = classNames(styles.setupRow, {
                      [styles.done]: done,
                    });
                    const handleClick = () => {
                      if (key === "mobileApps") {
                        try {
                          localStorage.setItem(mobileAppsStorageKey, "1");
                        } catch (_) {
                          // no-op
                        }
                        setMobileAppsDone(true);
                      }
                      // @ts-ignore custom analytics event
                      ReactGA.event("navbar_setup_item_clicked", {
                        companyId,
                        item: key,
                        path: itemPath || href,
                      });
                      setPopoverOpen(false);
                    };

                    const content = (
                      <>
                        <div className={styles.setupRowMain}>
                          {done ? (
                            <span className={styles.doneIndicator}>
                              <IconCheck size={10} stroke={3} />
                            </span>
                          ) : (
                            <span
                              className={classNames(
                                styles.setupIcon,
                                styles.pendingIndicator,
                              )}
                            />
                          )}
                          <div className={styles.setupRowText}>
                            <span className={styles.setupLabel}>
                              <FormattedMessage id={labelId} />
                            </span>
                            <span className={styles.setupDescription}>
                              <FormattedMessage id={descriptionId} />
                            </span>
                          </div>
                        </div>
                        <span className={styles.setupAction}>
                          <FormattedMessage
                            id={done ? "common.done" : "nav.aiSetup.action.go"}
                          />
                        </span>
                      </>
                    );

                    if (href) {
                      return (
                        <a
                          key={key}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={className}
                          onClick={handleClick}
                        >
                          {content}
                        </a>
                      );
                    }

                    return (
                      <Link
                        key={key}
                        to={itemPath || "/"}
                        className={className}
                        onClick={handleClick}
                      >
                        {content}
                      </Link>
                    );
                  }
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default NavBarAiItem;
